import { useLocale } from 'locale';
import React, { useCallback, useState } from 'react';
import { Button } from 'ds/components/button';
import * as Styled from './styled';
import CheckIcon from 'assets/svg/ok-check.svg';
import MailIcon from 'assets/svg/send.svg';
import { PoiId } from 'utils/entities';
import HebrewContactImage from 'components/unit-page/contact-agent/HebrewContactImageModeB';
import PhoneIcon from 'assets/svg/phone-new.svg';
import { Route } from 'config/routes';
import { PhoneButton } from 'components/phone-button';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { Link } from 'components/link';
import { AgentContactProps } from 'components/contact/types';
import { useScreenBreakpoint } from 'consts/breakpoints';
import _ from 'lodash';
import { ContactAgentModalSource, ContactModalOpenMeta } from 'store/state/app';
import useWhatsAppHref from 'hooks/useWhatsAppHref';
import ContactButtons from 'components/contact-buttons';
import ContactsWithWhatsAppExpandable from 'components/contact/whatsapp-expandable';


interface GalleryAgentStickyProps extends AgentContactProps {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

const getDataAutoStr = (sectionName: string) => `agent-gallery-sticky-${sectionName}`;

export const GalleryAgentSticky: React.FC<GalleryAgentStickyProps> = (props) => {
  const {
    isContacted,
    hideAgentInfo,
    showOnlyOffice,
    isDiscounted,
    agentId,
    officeId,
    agentName,
    officeName,
    isExclusive,
    onCallClickEvent,
    onSetContactModalPoiId,
    leadApi3Type,
    whatsappNumber,
    contactValues,
    phone,
    user,
    poi,
    id,
  } = props;

  const { t } = useLocale();
  const [ showPhone, setShowPhone ] = useState(false);
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const validPhoneNumber = Boolean(whatsappNumber);
  const whatsAppHref = useWhatsAppHref(whatsappNumber, agentName, id);

  const handleModalOpen = () => {
    onSetContactModalPoiId(id, { source: ContactAgentModalSource.FullScreenGallery });
  };

  const onShowPhone = useCallback((e: React.SyntheticEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || showPhone, { ...contactValues, id, type: poi.type }, ContactAgentModalSource.FullScreenGallery);
    if (!showPhone && !isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setShowPhone(true);
    }
  }, [ showPhone, setShowPhone, isMobile ]);

  if (isMobile) {
    return (
      <Styled.Root data-auto="agent-gallery-sticky">
        <Styled.ButtonWrapper justifyContent={isMobile} data-auto="agent-contact-gallery-sticky">
          <ContactsWithWhatsAppExpandable
            poi={poi}
            leadApi3Type={leadApi3Type}
            user={user}
            userPhone={contactValues.phone}
            whatsAppHref={whatsAppHref}
            whatsappNumber={whatsappNumber}
            validPhoneNumber={validPhoneNumber}
          >
            <PhoneButton onClick={onShowPhone} href={createPhoneURI(phone)} data-auto="agent-contact-gallery-button-sticky-text" size="extraLarge">
              <PhoneIcon width={24} height={24} />
              {showPhone ? phone : t('contactAgentModal.callButton')}
            </PhoneButton>
          </ContactsWithWhatsAppExpandable>
          {validPhoneNumber ? null : (
            <>
              {isContacted ? (
                <Button isInitialIconColor disabled data-auto="phone-private-inner-number-button-modal-disabled" size="extraLarge" mode="ghost">
                  <CheckIcon width={24} height={24} />
                  {t('contactAgentModal.button.disabled.modeB.static')}
                </Button>
              ) : (
                <Button isInitialIconColor onClick={handleModalOpen} data-auto="phone-private-inner-number-button-modal" size="extraLarge" mode="ghost">
                  <MailIcon width={24} height={24} />
                  {t('contactAgentModal.open')}
                </Button>
              )}
            </>
          )}
        </Styled.ButtonWrapper>
      </Styled.Root>
    );
  }

  return (
    <Styled.Root data-auto="agent-gallery-sticky">
      <Styled.Details>
        <HebrewContactImage data-auto={getDataAutoStr('image')} showOnlyOffice={showOnlyOffice} nonClickableAgent={isDiscounted} poc={(poi as any).poc} />
        <Styled.Texts>
          <Link routeName={showOnlyOffice ? Route.OfficePage : Route.AgentPage} routeParams={{ id: showOnlyOffice ? officeId : agentId, source: 'listing_page' }}>
            <Styled.AgentName weight="bold"  data-auto="poc-name">
              {showOnlyOffice ? officeName : agentName}
            </Styled.AgentName>
          </Link>
          {hideAgentInfo ? null : (
            <div>
              <Styled.AgentText>{t('unitPage.contactAgentHebrewExclusive', { exclusive: isExclusive })}</Styled.AgentText>
            </div>
          )}
        </Styled.Texts>
      </Styled.Details>
      <ContactButtons
        phone={phone}
        onShowPhone={onShowPhone}
        isContacted={isContacted}
        handleModalOpen={handleModalOpen}
        showPhone={showPhone}
        dataAttributePrefix="agent-gallery-sticky"
        source={ContactAgentModalSource.FullScreenGallery}
      />
    </Styled.Root>
  );
};
